<script lang="ts">
	//UI
	import {
		SlotSelect,
		HubProfileTextInput,
		DefaultButton,
	} from "@hitit/new_ui";
	import Message from "@hitit/hub/src/v7/components/profile/Message.svelte";
	import CancelButton from "@hitit/hub/src/v7/components/profile/CancelButton.svelte";
	import ProfileInputFrame from "@hitit/hub/src/v7/components/profile/ProfileInputFrame.svelte";
	import ProfileViewer from "@hitit/hub/src/v7/components/profile/ProfileViewer.svelte";
	//Utils
	import parsePhone from "libphonenumber-js";
	import { t, api } from "@hitit/hub/src/stores";
	import {
		addSpacer,
		getNationalPhonenumber,
		getInternationalPhonenumber,
	} from "@hitit/hub/src/functions/utils/phone";
	import { getCountries, getCountryCallingCode } from "libphonenumber-js";
	//Types
	import type { ProfileData, UserData } from "@hitit/api";
	//Props
	export let profile: ProfileData;
	export let user: UserData;
	//Constants
	const countryNames: Record<string, string> = $t("global.countries", {
		returnObjects: true,
	});

	//Variables
	let loading = false;
	let phonenumberCode = profile.cellphoneCountryCode || "82";
	let phonenumber = "";
	let editmode = false;
	let errorMessage = "";
	$: nationalNumber = getNationalPhonenumber(
		profile.cellphone,
		profile.cellphoneCountryCode
	);
	$: originCountryCode = profile.cellphoneCountryCode
		? `+${profile.cellphoneCountryCode}`
		: "";
	$: value = profile.cellphone?.includes("Azure")
		? profile.cellphone
		: `${originCountryCode} ${addSpacer(nationalNumber)}`;
	$: countries = getCountries().map((v) => ({
		label: `${countryNames[v.toLowerCase()]} +${getCountryCallingCode(v)}`,
		value: getCountryCallingCode(v) as string,
		display: `+${getCountryCallingCode(v)}`,
	}));

	$: saveButtonDisabled = errorMessage !== "" || phonenumber === "";
	$: backgroundColor = saveButtonDisabled ? "#a5c1f9" : "#6b93f3";
	//Functions
	function resetAll() {
		editmode = false;
		phonenumberCode = profile.cellphoneCountryCode || "82";
		phonenumber = "";
	}
	function onInput(e: Event) {
		const target = e.currentTarget as HTMLInputElement;
		const { value } = target;
		const originalCaret = target.selectionEnd;
		const originalGap = value.length - (originalCaret || 0);
		const replace = value
			.replace(/-/g, "") //"-"제거
			.replace(/[^0-9]/g, ""); //숫자를제거한 나머지 모두 제거
		const newValue = addSpacer(replace); //다시 addSpacer
		target.value = newValue;
		target.focus();

		//캐럿을 따로 설정해주지 않으면, target.value = newValue로 인해서 캐럿이 항상 마지막으로 이동한다.
		const newPosition = newValue.length - originalGap;
		target.setSelectionRange(newPosition, newPosition);

		phonenumber = replace;
		errorMessage = "";
	}

	async function postUserPhonenumber() {
		try {
			loading = true;
			const nationalPhoneNumber = getNationalPhonenumber(
				phonenumber,
				phonenumberCode
			);
			const tmpParsed = parsePhone(
				`+${phonenumberCode} ${nationalPhoneNumber}`
			);
			if (tmpParsed === undefined || !tmpParsed.isValid()) {
				errorMessage = "hub.profile.phonenumber_error";
				return;
			}
			const newProfile = Object.assign({}, profile, {
				cellphone: getInternationalPhonenumber(phonenumber, phonenumberCode),
				cellphoneCountryCode: phonenumberCode,
				cellphoneVerified: true,
			});
			const response = await $api.updateProfile(newProfile, user.id);
			profile = newProfile;
			resetAll();
		} catch (error) {
			errorMessage = "sso.error.unknownError";
			console.log(error);
		} finally {
			loading = false;
		}
	}
	//Do
</script>

<form class:editmode={editmode} on:submit|preventDefault={() => {}}>
	{#if !editmode}
		<div class="container">
			<ProfileViewer
				on:click={() => {
					editmode = true;
				}}
				name={$t("hub.profile.phonenumber")}
				value={value}
				editIcon={!(profile.cellphone?.includes("Azure"))}
			/>
		</div>
	{:else}
		<div class="edit-frame">
			<ProfileViewer
				value={value}
				name={$t("mbr.user.present_phonenumber")}
				editIcon={false}
			/>
			<ProfileInputFrame name={$t("mbr.user.new_phonenumber")}>
				<div class="select-box">
					<SlotSelect
						bind:value={phonenumberCode}
						let:displayValue
						let:noValue
						items={countries}
						placeholder={"+82"}
					>
						<span class:placeholder={noValue}>
							{noValue ? "+82" : displayValue}
						</span>
					</SlotSelect>
				</div>
				<HubProfileTextInput
					on:input={onInput}
					id="phonenumber"
					error={errorMessage !== ""}
					clearCallback={() => {
						phonenumber = "";
						errorMessage = "";
					}}
					value={phonenumber}
				/>
			</ProfileInputFrame>
			{#if errorMessage}
				<div class="error-box">
					<Message error messageTrnaslation={errorMessage} />
				</div>
			{/if}
		</div>
		<div class="button-box done" class:errorMessage={errorMessage}>
			<DefaultButton
				-phone-save={true}
				fitmode
				isLoading={loading}
				on:click={postUserPhonenumber}
				disabled={saveButtonDisabled}
				background={backgroundColor}
			>
				<span class="white">{$t("global.save")}</span>
			</DefaultButton>
		</div>
		<div class="cancelbox">
			<CancelButton on:click={() => resetAll()} />
		</div>
	{/if}
</form>

<style lang="scss">
	@import "../../../variables.scss";

	:global(.-phone-save) {
		display: flex;
		justify-content: center;
		align-items: center;
	}
	div {
		box-sizing: border-box;
	}

	form {
		display: flex;
		flex-direction: column;
		position: relative;
		padding: 0 20px;
		gap: 10px;

		&.editmode {
			background-color: #e6e7ed;
			padding: 20px 20px;
			.edit-frame {
				display: flex;
				flex-direction: column;
				gap: 20px;

				.error-box {
					margin-top: -10px;
				}

				.select-box {
					height: 31px;
					flex-basis: 60px;
					min-width: 60px;
					span {
						color: #46474b;
						padding-left: 10px;
						font-weight: 200;
						&.placeholder {
							color: #bcbfc3;
						}
					}
				}
			}
			.cancelbox {
				position: absolute;
				top: 10px;
				right: 10px;
			}
			.button-box {
				flex-grow: 1;
				height: 32px;

				&.done {
					.white {
						color: white;
						font-size: 1rem;
					}
				}
			}
		}
		.container {
			display: flex;
			flex-grow: 1;
			flex-direction: column;
			gap: 10px;
		}
	}

	@include DesktopSize {
		form {
			flex-direction: row;
			position: relative;
			padding: 0 40px;
			gap: 20px;

			.container {
				gap: 20px;
				.button-box {
					min-width: 150px;
					flex-basis: 150px;
					flex-grow: 0;
					box-sizing: border-box;
					height: 40px;

					&.done {
						.white {
							font-size: 1.25rem;
						}
					}
				}
			}

			&.editmode {
				padding: 20px 40px;
				.edit-frame {
					flex-grow: 1;
					.select-box {
						height: 40px;
						flex-basis: 100px;
						min-width: 100px;

						span {
							font-size: 1.25rem;
							font-weight: 500;
							&.placeholder {
								color: #bcbfc3;
							}
						}
					}
				}
				.button-box {
					display: flex;
					min-width: 150px;
					flex-basis: 150px;
					flex-grow: 0;
					box-sizing: border-box;
					height: 40px;

					&.done {
						margin-top: auto;
						.white {
							font-size: 1.25rem;
						}
					}

					&.errorMessage {
						margin-bottom: 26px;
					}
				}
				.cancelbox {
					position: absolute;
					top: 20px;
					right: 20px;
				}
			}
		}
	}
</style>
